import { ActionTree } from 'vuex';
import { LockService } from '@/services/LockService';
import {
  CabinetModel,
  DocumentToStoreModel,
  DocumentToStoreSearchModel,
  DocumentToStoreViewModel,
  ErrorModel,
  ExistingDocumentOption,
  LockModel,
  RootState,
  SchemaModel,
  DocumentToStoreState,
  EditReferencesMutationTypes,
  KeyValuePair,
  Dictionary,
  TargetDocumentTypeSettingsModel,
  PagesStoreMode
} from '@/types';
import { EditReferencesService } from '@/services';

export const actions: ActionTree<DocumentToStoreState, RootState> = {
  setDocumentToStoreView(this: any, { commit, getters }, payLoad: DocumentToStoreViewModel) {
    commit(EditReferencesMutationTypes.SetDocumentToStoreView, payLoad);

    const initialSchema = [...payLoad.dynamicFormSchema];
    const initialDocumentDate = getters.documentDateReferenceValue;
    commit(EditReferencesMutationTypes.SetInitialSchemaValues, { initialSchema, initialDocumentDate });
    commit(EditReferencesMutationTypes.SetInitialMultiValueReferences, Array.isArray(payLoad.multiValueReferences) ? [...payLoad.multiValueReferences] : []);
  },
  setDynamicFormSchema(this: any, { commit }, payLoad: SchemaModel[]) {
    commit(EditReferencesMutationTypes.SetDynamicFormSchema, payLoad);
  },
  setMultiValueReferenceValues(this: any, { commit }, payLoad: any[]) {
    commit(EditReferencesMutationTypes.SetMultiValueReferenceValues, payLoad);
  },
  clearMultiValueReferenceValues(this: any, { commit }) {
    commit(EditReferencesMutationTypes.SetMultiValueReferenceValues, []);
  },
  setIsLoading({ commit }, isLoading: boolean) {
    commit(EditReferencesMutationTypes.SetIsLoading, isLoading);
  },
  setIsDirty({ commit, dispatch }, isDirty: boolean) {
    dispatch('applicationDirtyStore/setApplicationDirty', isDirty, { root: true });
    commit(EditReferencesMutationTypes.SetIsDirty, isDirty);
  },
  setReadonly({ commit }, isReadonly: boolean) {
    commit(EditReferencesMutationTypes.SetIsReadonly, isReadonly);
  },
  getDocumentToStore({ commit }, payload: DocumentToStoreSearchModel) {
    commit(EditReferencesMutationTypes.SetIsLoading, true);
    commit(EditReferencesMutationTypes.SetExistingDocumentId, 0);
    commit(EditReferencesMutationTypes.SetExistingDocumentOption, ExistingDocumentOption.AddToDocument);
    commit(EditReferencesMutationTypes.SetPageViewedBeforeAddToExisting, 1);

    const initialDocumentToStoreView = {
      cabinets: [] as CabinetModel[],
      dynamicFormSchema: [] as SchemaModel[],
      targetDocumentTypeSettings: {} as TargetDocumentTypeSettingsModel
    } as DocumentToStoreViewModel;

    commit(EditReferencesMutationTypes.SetDocumentToStoreView, initialDocumentToStoreView);

    EditReferencesService.getDocumentToStoreAsync(payload.sourceCabinetId, payload.sourceDocumentId)
      .then((response: DocumentToStoreModel) => {
        commit(EditReferencesMutationTypes.DocumentToStoreLoaded, response);
      })
      .catch((error: any) => {
        commit(EditReferencesMutationTypes.DocumentToStoreError, error);
      });
  },
  extendLock({ commit, getters }) {
    const currentLock = getters.entityLock as LockModel | undefined;
    if (currentLock && currentLock.isLockedByCurrentSessionId) {
      commit(EditReferencesMutationTypes.SetExtendingDocumentLock, true);
      LockService.extendLockAsync(currentLock.id)
        .then((response: LockModel) => {
          const payload: LockModel = response;
          commit(EditReferencesMutationTypes.ExtendDocumentLock, payload);
          commit(EditReferencesMutationTypes.SetExtendingDocumentLock, false);
        })
        .catch((error: any) => {
          commit(EditReferencesMutationTypes.ExpireDocumentLock, error);
          commit(EditReferencesMutationTypes.SetExtendingDocumentLock, false);
        });
    }
  },
  releaseDocumentToStoreLock({ commit, getters }) {
    const currentLock = getters.entityLock as LockModel | undefined;
    if (currentLock && currentLock.isLockedByCurrentSessionId) {
      LockService.releaseLockAsync(currentLock.id);
    }
  },
  setExtendingLock({ commit }, payload: boolean) {
    commit(EditReferencesMutationTypes.SetExtendingDocumentLock, payload);
  },
  setComponentName({ commit }, componentName: string) {
    commit(EditReferencesMutationTypes.SetComponentName, componentName);
  },
  setErrorModel({ commit }, errorModel: ErrorModel) {
    commit(EditReferencesMutationTypes.SetErrorModel, errorModel);
  },
  setSchemaValuesFromReferences({ commit }, references: Dictionary<string>) {
    const modelKeys = Object.getOwnPropertyNames(references);
    modelKeys.forEach((k: string) => {
      const reference = { key: k, value: references[k] } as KeyValuePair<string>;
      commit(EditReferencesMutationTypes.SetSchemaValueFromReference, reference);
    });
  },
  setSchemaValuesFromRetainedFields({ commit, state }, fields: SchemaModel[]) {
    const setFieldValue = (field: SchemaModel) => {
      const reference = { key: field.id, value: field.value } as KeyValuePair<string>;
      commit(EditReferencesMutationTypes.SetSchemaValueFromReference, reference);
    };
    const documentReferenceMatches = (field: SchemaModel): boolean => {
      const referenceIndex = state.documentToStoreView.dynamicFormSchema.findIndex(
        (schema: SchemaModel) => schema.id.toLowerCase() === field.id.toLowerCase() && schema.label.toLowerCase() === field.label.toLowerCase()
      );
      return referenceIndex !== -1;
    };
    const fieldsByType = (typePrefix: string): SchemaModel[] => {
      return fields.filter((field: SchemaModel) => field.id.toLowerCase().startsWith(typePrefix));
    };
    if (state.documentToStoreView.targetDocumentTypeSettings.retainCommonReferenceValues) {
      fieldsByType('cref').forEach((commonField: SchemaModel) => {
        setFieldValue(commonField);
      });
    }
    if (state.documentToStoreView.targetDocumentTypeSettings.retainDocumentReferenceValues) {
      fieldsByType('dref').forEach((documentField: SchemaModel) => {
        if (documentReferenceMatches(documentField)) {
          setFieldValue(documentField);
        }
      });
    }
  },
  setDocumentDateValue({ commit }, documentDate: Date | null) {
    commit(EditReferencesMutationTypes.SetDocumentDateValue, documentDate);
  },
  setSourceDocumentTypeId({ commit }, documentTypeId: number) {
    commit(EditReferencesMutationTypes.SetSourceDocumentTypeId, documentTypeId);
  },
  setTargetDocumentTypeId({ commit }, documentTypeId: number) {
    commit(EditReferencesMutationTypes.SetTargetDocumentTypeId, documentTypeId);
  },
  selectExistingDocument({ commit }, pageViewedBeforeAddToExisting: number) {
    commit(EditReferencesMutationTypes.SetPageViewedBeforeAddToExisting, pageViewedBeforeAddToExisting);
    commit(EditReferencesMutationTypes.SetComponentName, 'SelectExistingDocument');
  },
  setExistingDocumentId({ commit }, documentId: number) {
    commit(EditReferencesMutationTypes.SetExistingDocumentId, documentId);
  },
  setExistingDocumentOption({ commit }, option: ExistingDocumentOption) {
    commit(EditReferencesMutationTypes.SetExistingDocumentOption, option);
  },
  clearExistingDocument({ commit }) {
    commit(EditReferencesMutationTypes.SetExistingDocumentId, 0);
    commit(EditReferencesMutationTypes.SetExistingDocumentOption, ExistingDocumentOption.AddToDocument);
  },
  setSelectedPages({ state, commit }, payload: string) {
    commit(EditReferencesMutationTypes.SetSelectedPages, payload);
  },
  setPagesStoreMode({ state, commit }, payload: PagesStoreMode) {
    commit(EditReferencesMutationTypes.SetPagesStoreMode, payload);
  }
};
